import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import type { IApiContentResponseModel, ProductVariantDto } from '@/services/platform/api/model';
import type { AppContext } from '@/lib/core/types/modules';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';
import { useCartStore, useLocaleStore } from '@/lib/core/stores';
import { cn, formatAmount, getImageUrl, parseShopUrlSegments } from '@/lib/core/utils';
import { useApp } from '@/lib/core/providers/appProvider';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import {
  useShopProduct,
  useRelatedProducts,
  useRelatedProductsForBag,
  useFavoriteLists,
} from '@/services/platform/data/shop';
import { ScreenHead } from '@/components/shared/screen/head';
import { ShopHeaderActions } from '@/components/shared/shop/layout/ShopHeaderActions';
import { MiniCardCarousel } from '../../ui/MiniCardCarousel';
import {
  ProductFavoriteToggle,
  type ProductFavoriteToggleHandle,
} from '../../wishlist/ui/ProductFavoriteToggle';
import { ShopMiniCard } from '../../section/mini-card';
import { Toast, ToastErrorIcon } from '@/components/ui/toast';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { NotFoundError } from '@/components/shared/errors';
import { mapCartErrorMessage } from '@/lib/core/shop/cartErrors';
import { ProductGallery } from './sections/ProductGallery';
import { ProductInfoAccordion } from './sections/ProductInfoAccordion';
import { ChooseSizeSheet } from './sheets/ChooseSizeSheet';
import { ChooseQuantitySheet } from './sheets/ChooseQuantitySheet';
import { ItemAddedSheet } from './sheets/ItemAddedSheet';

interface ShopProductDetailScreenProps {
  content: IApiContentResponseModel;
  context?: AppContext;
}

/**
 * Full product detail screen. Resolves the product from the `product_<guid>`
 * URL segment, fetches the DTO from the catalog API, and lays out gallery,
 * info block, expandable sections, related products, and the sticky add-to-bag
 * bar. Choose-size and item-added flows are local sheet state.
 */
export function ShopProductDetailScreen({ content, context }: ShopProductDetailScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const addItem = useCartStore(state => state.addItem);
  const cartItems = useCartStore(state => state.items);
  const { appSettings } = useApp();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  const segments = parseShopUrlSegments(location.pathname);
  const { productId } = segments;
  const shopSectionSlug = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const shopIndex = parts.indexOf('shop');
    return parts[shopIndex + 1] ?? '';
  }, [location.pathname]);
  const { data: product, isLoading, hasFetched } = useShopProduct(productId);
  const isQuantityFlow = product?.sections?.some(section => section.isQuantityFlow) ?? false;
  const { data: related } = useRelatedProducts(product?.publicGuid);
  const { data: favoriteLists, refetch: refetchFavoriteLists } = useFavoriteLists();
  const shopFavoriteLists = useMemo(() => favoriteLists.filter(l => l.listType === 'Shop'), [favoriteLists]);
  const favoriteToggleRef = useRef<ProductFavoriteToggleHandle>(null);
  const [isFavorited, setIsFavorited] = useState(false);

  const [isSizeSheetOpen, setSizeSheetOpen] = useState(false);
  const [isQuantitySheetOpen, setQuantitySheetOpen] = useState(false);
  const [isAddedSheetOpen, setAddedSheetOpen] = useState(false);
  const [addedItemGuid, setAddedItemGuid] = useState<string | undefined>(undefined);
  const [cartErrorMessage, setCartErrorMessage] = useState<string | null>(null);
  const [cartErrorToastKey, setCartErrorToastKey] = useState(0);

  // Only fetch once the sheet actually opens — avoids an extra request on every product view.
  const { data: relatedForBag } = useRelatedProductsForBag(
    isAddedSheetOpen ? product?.publicGuid : undefined
  );

  const firstVariant = product?.variants?.[0];
  const displayPrice = formatAmount(firstVariant?.price ?? null, null, locale ?? 'en-GB');
  const sku = firstVariant?.sku;

  const isNewProduct = product?.isNew === true;

  const sizeChartUrl = getImageUrl(appSettings?.properties?.sizeChartFile?.[0]);

  const openSizeChart = async () => {
    if (!sizeChartUrl) return;
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url: sizeChartUrl });
    } else {
      window.open(sizeChartUrl, '_blank');
    }
  };

  const handleSelectVariant = async (variant: ProductVariantDto) => {
    setSizeSheetOpen(false);
    if (!variant.publicGuid) return;
    const result = await addItem(variant.publicGuid, 1);
    if (!result.success) {
      setCartErrorMessage(mapCartErrorMessage(t, result.error));
      setCartErrorToastKey(k => k + 1);
      return;
    }
    setAddedItemGuid(variant.publicGuid);
    setAddedSheetOpen(true);
  };

  const handleConfirmQuantity = async (quantity: number) => {
    setQuantitySheetOpen(false);
    if (!firstVariant?.publicGuid) return;
    const result = await addItem(firstVariant.publicGuid, quantity);
    if (!result.success) {
      setCartErrorMessage(mapCartErrorMessage(t, result.error));
      setCartErrorToastKey(k => k + 1);
      return;
    }
    setAddedItemGuid(firstVariant.publicGuid);
    setAddedSheetOpen(true);
  };

  const addedItem = useMemo(() => {
    if (!addedItemGuid) return cartItems[cartItems.length - 1];
    return cartItems.find(i => i.variantId === addedItemGuid) ?? cartItems[cartItems.length - 1];
  }, [cartItems, addedItemGuid]);

  const goBack = () => navigate(-1);

  if (isLoading || !hasFetched) {
    return <SpinLoading wrapperClassName="min-h-[50vh]" />;
  }
  if (!product) {
    return <NotFoundError />;
  }

  const images = product.images ?? [];

  return (
    <div className="relative w-full" data-content={content?.name}>
      <div className="relative w-full">
        <div
          className={cn(
            'pt-safe-area fixed top-0 z-80 flex w-full',
            'bg-linear-to-b from-black/80 to-transparent pb-4 backdrop-blur-[2.5px]'
          )}
        >
          <ScreenHead
            title=""
            onBack={goBack}
            ariaLabel={t('App.Labels.GoBack')}
            variant="overlay"
            className="pt-0"
            rightActions={
              <ShopHeaderActions
                context={context}
                product={product}
                hideSearch
                showShare
                showHeroCarousel={true}
              />
            }
          />
        </div>
      </div>

      <ProductGallery images={images} alt={product.title ?? ''} />

      <div className="flex flex-col gap-6 px-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {product.vendor && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-4 font-semibold tracking-[0.5px] uppercase">
                {product.vendor}
              </p>
            )}
            {product.title && (
              <h1 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] leading-normal font-normal">
                {product.title}
              </h1>
            )}
          </div>
          <ProductFavoriteToggle
            ref={favoriteToggleRef}
            product={{
              publicGuid: product.publicGuid,
              title: product.title,
              vendor: product.vendor,
              imageUrl: product.images?.[0]?.src,
            }}
            favoriteLists={shopFavoriteLists}
            refetchFavoriteLists={refetchFavoriteLists}
            size={24}
            className="mt-1 shrink-0"
            onFavoritedChange={setIsFavorited}
          />
        </div>

        {product.bodyHtml && (
          <div
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-normal font-[350] tracking-[0.5px]"
            dangerouslySetInnerHTML={{ __html: extractShortDescription(product.bodyHtml) }}
          />
        )}

        {isNewProduct && (
          /* Design: 40px above the tag, 16px below it — offsets on top of the 24px column gap. */
          <span className="border-jncquoi-neutral-1000 text-jncquoi-neutral-1000 font-pp-neue-montreal mt-4 -mb-2 inline-block w-fit border-[0.5px] px-3 py-1.5 text-[10px] leading-none font-medium tracking-[0.5px] uppercase">
            {t('App.Shop.New')}
          </span>
        )}

        <ProductInfoAccordion
          descriptionHtml={product.bodyHtml}
          deliveryReturnsHtml={appSettings?.properties?.deliveryAndReturns?.markup ?? null}
        />

        {sku && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[11px] leading-none tracking-[0.22px]">
            {t('App.Shop.SKU')}: {sku}
          </p>
        )}
      </div>

      {related.length > 0 && (
        <section className="mt-10 flex flex-col gap-6 pl-4">
          <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] leading-none font-semibold">
            {t('App.Shop.YouMayAlsoLike')}
          </h2>
          <MiniCardCarousel
            items={related}
            ariaLabel={t('App.Shop.YouMayAlsoLike')}
            renderItem={p => (
              <ShopMiniCard
                variant="product"
                image={p.imageUrl ?? undefined}
                brand={p.vendor ?? undefined}
                name={p.title ?? undefined}
                onClick={() => {
                  if (!p.publicGuid) return;
                  const localeSegment = locale?.split('-')[0] ?? 'en';
                  navigate(`/${localeSegment}/shop/${shopSectionSlug}/discover/product_${p.publicGuid}`);
                }}
              />
            )}
          />
        </section>
      )}

      <div aria-hidden="true" className="h-[calc(78px+env(safe-area-inset-bottom))]" />

      <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-300 fixed right-0 bottom-0 left-0 z-30 border-t-[0.5px] px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-4">
          <Button
            variant="primary-solid"
            palette="dark"
            size="md"
            className="flex-1"
            onClick={() => (isQuantityFlow ? setQuantitySheetOpen(true) : setSizeSheetOpen(true))}
          >
            {t('App.Shop.AddToBag')}
          </Button>
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-right text-[12px] leading-none font-semibold tracking-[0.5px] uppercase">
            {displayPrice}
          </span>
        </div>
      </div>

      {isQuantityFlow ? (
        <ChooseQuantitySheet
          open={isQuantitySheetOpen}
          onOpenChange={setQuantitySheetOpen}
          maxQuantity={firstVariant?.stock ?? 0}
          onConfirm={handleConfirmQuantity}
        />
      ) : (
        <ChooseSizeSheet
          open={isSizeSheetOpen}
          onOpenChange={setSizeSheetOpen}
          variants={product.variants ?? []}
          onSelect={handleSelectVariant}
          onOpenSizeChart={sizeChartUrl ? openSizeChart : undefined}
        />
      )}

      <ItemAddedSheet
        open={isAddedSheetOpen}
        onOpenChange={setAddedSheetOpen}
        item={addedItem}
        suggestions={relatedForBag}
        shopSectionSlug={shopSectionSlug}
        productGuid={product.publicGuid}
        isFavorited={isFavorited}
        onToggleFavorite={() => favoriteToggleRef.current?.open()}
      />

      {/* Keeps the neutral `dark` background with the xmark glyph, as before. */}
      <Toast
        key={cartErrorToastKey}
        open={cartErrorMessage !== null}
        message={cartErrorMessage ?? ''}
        icon={ToastErrorIcon}
        onDismiss={() => setCartErrorMessage(null)}
      />
    </div>
  );
}

/**
 * The catalog `bodyHtml` field lumps description + care + delivery notes together.
 * For the info block above the accordion we want a short blurb, so we grab the
 * first paragraph. The full HTML remains inside the accordion `Description`.
 */
function extractShortDescription(html: string): string {
  const match = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  return match?.[0] ?? html;
}
